import type {
  GuidedEditCreativeApi,
  InstagramPartnerPermissionStatusWire,
} from "../interfaces/adsManagerGuidedEdit";
import { partnershipPartnerFacebookInstagramMismatchMessage } from "./partnershipPartnerOptions";
import {
  isApprovedBrandedContentPermissionStatus,
  isPublishBlockedPartnershipPermissionStatus,
} from "./metaPartnershipAdCreative";
import type { MetaBrandedContentAdPermissionStatus } from "../interfaces/MetaTypes";
import type {
  MarketingAd,
  MarketingAdCreative,
  MarketingCampaign,
} from "../interfaces/KYDMarketing";

/** Persisted shape when the user clears partnership partner identity on save. */
export const DISABLED_PARTNERSHIP: NonNullable<
  MarketingAdCreative["partnership"]
> = { enabled: false };

export const INSTAGRAM_PARTNER_USER_ID_REQUIRED_MESSAGE =
  "Select a partner Facebook page or Instagram account for partnership ads.";

export function toInstagramPartnerPermissionStatusWire(
  status: MetaBrandedContentAdPermissionStatus | undefined
): InstagramPartnerPermissionStatusWire {
  const normalized = status?.trim().toUpperCase();
  if (normalized === "APPROVED") {
    return "APPROVED";
  }
  if (normalized === "PENDING") {
    return "PENDING";
  }
  if (normalized === "REVOKED") {
    return "REVOKED";
  }
  return "UNKNOWN";
}

export const INSTAGRAM_PARTNER_APPROVAL_REQUIRED_MESSAGE =
  "Partnership must be approved in Meta before you can launch. Request partnership or choose an approved partner.";

/** Launch/publish block message (null when approved). */
export function instagramPartnerPermissionLaunchMessage(
  status: InstagramPartnerPermissionStatusWire | undefined
): string | null {
  if (status === "PENDING") {
    return "Partnership approval is pending. The partner must accept in Instagram or Facebook before Meta can publish this ad.";
  }
  if (status === "REVOKED") {
    return "Partnership was revoked in Meta. Request partnership again or choose another partner before publishing.";
  }
  if (!isApprovedBrandedContentPermissionStatus(status)) {
    return INSTAGRAM_PARTNER_APPROVAL_REQUIRED_MESSAGE;
  }
  return null;
}

const PARTNER_PERMISSION_UI: Record<
  InstagramPartnerPermissionStatusWire,
  { label: string; badgeColorScheme: string }
> = {
  APPROVED: { label: "Approved", badgeColorScheme: "green" },
  PENDING: { label: "Pending", badgeColorScheme: "yellow" },
  REVOKED: { label: "Revoked", badgeColorScheme: "red" },
  UNKNOWN: { label: "Unknown", badgeColorScheme: "gray" },
};

export function instagramPartnerPermissionUi(
  status: InstagramPartnerPermissionStatusWire | undefined
): { label: string; badgeColorScheme: string } {
  return PARTNER_PERMISSION_UI[status ?? "UNKNOWN"];
}

/** Form / wire: partnership is active when a partner is selected or persisted as enabled. */
export function creativeBlockHasPartnerPost(creative: {
  partnerPostEnabled?: boolean;
  partnerInstagramUserId?: string;
  partnerInstagramUsername?: string;
  partnerFacebookPageId?: string;
}): boolean {
  if (creative.partnerInstagramUserId?.trim()) {
    return true;
  }
  if (creative.partnerInstagramUsername?.trim()) {
    return true;
  }
  if (creative.partnerFacebookPageId?.trim()) {
    return true;
  }
  return Boolean(creative.partnerPostEnabled);
}

/** Resolved partner identity required to save or publish (IG user id and/or FB page id). */
export function creativeBlockHasPartnershipPartnerIdentity(creative: {
  partnerInstagramUserId?: string;
  partnerFacebookPageId?: string;
}): boolean {
  return Boolean(
    creative.partnerInstagramUserId?.trim() || creative.partnerFacebookPageId?.trim()
  );
}

export function guidedWireHasPartnerPost(
  creative: Pick<
    GuidedEditCreativeApi,
    | "partner_post_enabled"
    | "partner_instagram_user_id"
    | "partner_instagram_username"
    | "partner_facebook_page_id"
  >
): boolean {
  return creativeBlockHasPartnerPost({
    partnerPostEnabled: creative.partner_post_enabled,
    partnerInstagramUserId: creative.partner_instagram_user_id,
    partnerInstagramUsername: creative.partner_instagram_username,
    partnerFacebookPageId: creative.partner_facebook_page_id,
  });
}

function guidedWireHasPartnershipPartnerIdentity(
  creative: GuidedEditCreativeApi
): boolean {
  return creativeBlockHasPartnershipPartnerIdentity({
    partnerInstagramUserId: creative.partner_instagram_user_id,
    partnerFacebookPageId: creative.partner_facebook_page_id,
  });
}


export function partnershipFromGuidedWire(
  creative: GuidedEditCreativeApi
): MarketingAdCreative["partnership"] | undefined {
  if (!guidedWireHasPartnerPost(creative)) {
    return undefined;
  }
  return {
    enabled: true,
    partner_instagram_user_id: creative.partner_instagram_user_id?.trim() || undefined,
    partner_instagram_username:
      creative.partner_instagram_username?.trim() || undefined,
    permission_status: creative.partner_permission_status,
    partner_facebook_page_id:
      creative.partner_facebook_page_id?.trim() || undefined,
  };
}

export function applyPartnershipToGuidedWire(
  creative: MarketingAdCreative | undefined
): Pick<
  GuidedEditCreativeApi,
  | "partner_post_enabled"
  | "partner_instagram_user_id"
  | "partner_instagram_username"
  | "partner_permission_status"
  | "partner_facebook_page_id"
> {
  const partnership = creative?.partnership;
  if (!partnership?.enabled) {
    return { partner_post_enabled: false };
  }
  return {
    partner_post_enabled: true,
    partner_instagram_user_id: partnership.partner_instagram_user_id,
    partner_instagram_username: partnership.partner_instagram_username,
    partner_permission_status: (partnership.permission_status ??
      "UNKNOWN") as GuidedEditCreativeApi["partner_permission_status"],
    partner_facebook_page_id: partnership.partner_facebook_page_id,
  };
}

export function validatePartnerPostForLaunch(
  creatives: readonly GuidedEditCreativeApi[]
): string | null {
  for (const creative of creatives) {
    if (!guidedWireHasPartnerPost(creative)) {
      continue;
    }
    if (!guidedWireHasPartnershipPartnerIdentity(creative)) {
      return INSTAGRAM_PARTNER_USER_ID_REQUIRED_MESSAGE;
    }
    const mismatchErr = partnershipPartnerFacebookInstagramMismatchMessage({
      partnerFacebookPageId: creative.partner_facebook_page_id,
      partnerInstagramUserId: creative.partner_instagram_user_id,
      linkedInstagramUserId:
        creative.partner_facebook_linked_instagram_user_id,
    });
    if (mismatchErr) {
      return mismatchErr;
    }
    const permissionErr = partnershipPermissionLaunchBlockMessage(
      creative.partner_permission_status
    );
    if (permissionErr) {
      return permissionErr;
    }
  }
  return null;
}

function partnershipPermissionLaunchBlockMessage(
  status: InstagramPartnerPermissionStatusWire | undefined
): string | null {
  if (!isPublishBlockedPartnershipPermissionStatus(status)) {
    return null;
  }
  return instagramPartnerPermissionLaunchMessage(status);
}

export function isPartnershipApprovedForPublish(
  creative: MarketingAdCreative | undefined
): boolean {
  const partnership = creative?.partnership;
  if (!partnership?.enabled) {
    return false;
  }
  const hasPartnerIdentity = Boolean(
    partnership.partner_instagram_user_id?.trim() ||
      partnership.partner_facebook_page_id?.trim()
  );
  if (!hasPartnerIdentity) {
    return false;
  }
  return isApprovedBrandedContentPermissionStatus(partnership.permission_status);
}

export function validatePartnerPostFromMarketingAd(
  ad: MarketingAd
): string | null {
  const partnership = ad.creative?.partnership;
  if (!partnership?.enabled) {
    return null;
  }
  if (
    !partnership.partner_instagram_user_id?.trim() &&
    !partnership.partner_facebook_page_id?.trim()
  ) {
    return INSTAGRAM_PARTNER_USER_ID_REQUIRED_MESSAGE;
  }
  const permissionErr = partnershipPermissionLaunchBlockMessage(
    partnership.permission_status as InstagramPartnerPermissionStatusWire | undefined
  );
  if (permissionErr) {
    return permissionErr;
  }
  return null;
}

export function validatePartnerPostFromCampaign(
  campaign: MarketingCampaign
): string | null {
  for (const adset of campaign.adsets ?? []) {
    for (const ad of adset.ads ?? []) {
      const err = validatePartnerPostFromMarketingAd(ad);
      if (err) return err;
    }
  }
  return null;
}
